import Transport from 'winston-transport';
import { jwtHelperMyStage } from '@/utils/jwt';
import store from '@/store/index';
import GelfLogger from '@/modules/myStageLog/GelfLogger';
import LogEventStore from '@/modules/myStageLog/LogEventStore';
import backendLogger from '@/modules/myStageLog/BackendLogger';

/** Class representing the Bizmates Winston Transport
 * reference: https://github.com/winstonjs/winston#adding-custom-transports
*/
class BizTransport extends Transport {
  /**
   * Create the transport
   * @param {Object} options - The options for configuring the transport.
   */
  constructor(options) {
    super(options);

    options = options || {};
    this.lessonToken = options.lessonToken || null;
    this.jwtKeyValue = options.jwtKeyValue || null;

    /**
     * RFC 5424
     * refer: https://www.rfc-editor.org/rfc/rfc5424.txt
     *  0       Emergency: system is unusable
     *  1       Alert: action must be taken immediately
     *  2       Critical: critical conditions
     *  3       Error: error conditions
     *  4       Warning: warning conditions
     *  5       Notice: normal but significant condition
     *  6       Informational: informational messages
     *  7       Debug: debug-level messages
     */
    this.postLevels = {
      error: 3,
      warn: 4,
      notice: 5,
      info: 6,
      debug: 7
    }

    /**
     *  Default Result Code
     */
    this.resultCode = {
      success: 0,
      error: -1
    }

    /**
     * Limit to number of logs to temporarily keep in store
     */
    this.logLimit = 30;

    this.gelfLogger = new GelfLogger();
    this.logEventStore = new LogEventStore();
    this.backendLogger = backendLogger;
  }


  /**
   * Log a message to Bizmates.jp log api.
   *
   * @param {Object} info - An object containing the `message`, `level`, `Symbol(level)`, `Symbol(message)`.
   * @param {function} callback - Winston's callback to itself.
   */
  log(info, callback) {
    let postInfo = this.prepareLogPayload(info);

    if (postInfo.op && postInfo.op.endsWith(':server-log')) {
      postInfo.op = postInfo.op.replace(/:server-log$/, '');
      this.backendLogger.send(postInfo);
      callback();
      return;
    }

    if (jwtHelperMyStage.isGracePeriodEnd(this.lessonToken) && !this.jwtKeyValue) {
      return false;
    }

    if (store.getters.isLessonEnded) {
      return false;
    }

    // DEBUG: (please don't delete these lines, they might help someone later debugging this code)
    // console.log('Original info:', info);
    // console.log('POST payload to Bizmates.jp API:', postInfo);

    /*
    * GELF logging [New method of sending logs]
    * Send logs through the GELF handler instead of the default API/batch process
    * This emits logs via socket.io as proxy before sending via GELF to Graylog
    */
    if (!store.state.isTest) {
      this.logEventStore.send(postInfo);
    }
    return this.gelfLogger.log(postInfo, callback, this.emit.bind(this));
  }

  /*
  * Processes and formats the log payload into the GELF structure
  */
  prepareLogPayload(info) {
    let infoCopy = JSON.parse(JSON.stringify(info));
    let payload = {};

    if (infoCopy.hasOwnProperty('message')) {
      payload.msg = infoCopy.message;
      delete infoCopy.message;
    }

    if (infoCopy.hasOwnProperty('result')) {
      payload.res = infoCopy.result;
      delete infoCopy.result;
    } else {
      payload.res = info.level === 'error' ? this.resultCode.error : this.resultCode.success;
    }

    if (infoCopy.hasOwnProperty('level')) {
      payload.sev = this.postLevels[info.level];
      delete infoCopy.level;
    }

    if (infoCopy.hasOwnProperty('operation')) {
      payload.op = infoCopy.operation;
      delete infoCopy.operation;
      this.logLimit = (payload.op == 'send-batch-log' ? 1 : 30);
    }

    if (Object.keys(infoCopy).length) {
      payload.rsn = infoCopy; // assign all other JSON fields (if any) to the `rsn` field of POST payload JSON
    }

    return payload;
  }
}

export default BizTransport;
